import {AppConfig} from '@frontend/AppConfig'
import {appUrl} from '@frontend/AppUrl'
import {Blinker} from '@frontend/components/Blinker'
import {ResponsiveLayout} from '@frontend/components/ResponsiveLayout'
import {trans} from '@frontend/helpers'
import {useUserData} from '@frontend/hooks'
import {useIsDesktopScreen} from '@frontend/hooks/useIsDesktopScreen'
import {
    useAccountFlagsStore,
    useAuthStore,
} from '@frontend/stores'
import {
    mdiChevronDown,
    mdiMessageQuestionOutline,
    mdiMessageTextOutline,
} from '@mdi/js'
import {useState} from 'react'
import {
    Link,
    NavLink,
} from 'react-router-dom'
import {Card} from 'swayok-react-mdb-ui-kit/components/Card/Card'
import {CardBody} from 'swayok-react-mdb-ui-kit/components/Card/CardBody'
import {CardFooter} from 'swayok-react-mdb-ui-kit/components/Card/CardFooter'
import {Collapse} from 'swayok-react-mdb-ui-kit/components/Collapse/Collapse'
import {Icon} from 'swayok-react-mdb-ui-kit/components/Icon/Icon'
import {IconButton} from 'swayok-react-mdb-ui-kit/components/Icon/IconButton'
import {RatingStars} from 'swayok-react-mdb-ui-kit/components/Icon/RatingStars'
import {NavigationService} from 'swayok-react-mdb-ui-kit/services/NavigationService'
import {CabinetUserReviewsButton} from './CabinetUserReviewsButton'

// Левое меню в ЛК пользователя с информацией о профиле и ссылками на разделы ЛК.
export function CabinetProfileInfoMenu() {

    const user = useUserData()
    const hasNewChatMessages = useAccountFlagsStore(
        state => state.hasNewChatMessages
    )
    const hasNewSupportTicketReplies = useAccountFlagsStore(
        state => state.hasNewSupportTicketReplies
    )
    const logout = useAuthStore(
        state => state.logout
    )

    const isDesktop = useIsDesktopScreen()

    // Видимость блока со ссылками на разделы ЛК.
    const [
        isFooterVisible,
        setIsFooterVisible,
    ] = useState<boolean>(isDesktop)

    if (!user) {
        return null
    }

    const menuContent = (
        <>
            <CardFooter className="cabinet-menu-group">
                <div>
                    <NavLink
                        to={appUrl().cabinet.ads.index}
                        className="side-nav-link"
                    >
                        {trans().cabinet.side_navigation.my_ads}
                    </NavLink>
                </div>
                <div>
                    <NavLink
                        to={appUrl().cabinet.executors.index}
                        className="side-nav-link"
                    >
                        {trans().cabinet.side_navigation.my_services}
                    </NavLink>
                </div>
                <div>
                    <NavLink
                        to={appUrl().cabinet.reviews}
                        className="side-nav-link"
                    >
                        {trans().cabinet.side_navigation.my_reviews}
                    </NavLink>
                </div>
                <div>
                    <NavLink
                        to={appUrl().cabinet.favorites}
                        className="side-nav-link"
                    >
                        {trans().cabinet.side_navigation.my_favorites}
                    </NavLink>
                </div>
                <div>
                    <NavLink
                        to={appUrl().cabinet.subscriptions}
                        className="side-nav-link"
                    >
                        {trans().cabinet.side_navigation.my_subscriptions}
                    </NavLink>
                </div>
            </CardFooter>
            <CardFooter className="cabinet-menu-group">
                <div className="flex-row-between">
                    <NavLink
                        to={appUrl().cabinet.chats.index}
                        className="side-nav-link"
                    >
                        {trans().cabinet.side_navigation.my_chats}
                    </NavLink>
                    {hasNewChatMessages && (
                        <Blinker repeats={3}>
                            <Icon
                                path={mdiMessageTextOutline}
                                tooltip={trans().cabinet.side_navigation.has_new_chat_messages}
                                size={20}
                                color="blue"
                            />
                        </Blinker>
                    )}
                </div>
                <div>
                    <NavLink
                        to={appUrl().cabinet.notifications.index}
                        className="side-nav-link"
                    >
                        {trans().cabinet.side_navigation.notifications}
                    </NavLink>
                </div>
                <div className="flex-row-between">
                    <NavLink
                        to={appUrl().cabinet.supportTickets.index}
                        className="side-nav-link"
                    >
                        {trans().cabinet.side_navigation.support_tickets}
                    </NavLink>
                    {hasNewSupportTicketReplies && (
                        <Blinker repeats={3}>
                            <Icon
                                path={mdiMessageQuestionOutline}
                                tooltip={trans().cabinet.side_navigation.has_new_support_ticket_replies}
                                size={20}
                                color="orange"
                            />
                        </Blinker>
                    )}
                </div>
            </CardFooter>
            <CardFooter className="cabinet-menu-group">
                <div>
                    <NavLink
                        to={NavigationService.makeUrl(
                            appUrl().cabinet.advancePayments.surveyForm,
                            null,
                            {step: '1'}
                        )}
                        className="side-nav-link"
                    >
                        {trans().cabinet.side_navigation.client_data}
                    </NavLink>
                </div>
                {AppConfig.features?.advances?.enabled && (
                    <div>
                        <NavLink
                            to={appUrl().cabinet.advancePayments.contract}
                            className="side-nav-link"
                        >
                            {trans().cabinet.side_navigation.active_contract}
                        </NavLink>
                    </div>
                )}
                <div>
                    <NavLink
                        to={appUrl().cabinet.transactions.index}
                        className="side-nav-link"
                    >
                        {trans().cabinet.side_navigation.transactions}
                    </NavLink>
                </div>
            </CardFooter>
            <CardFooter className="cabinet-menu-group">
                <div>
                    <NavLink
                        to={appUrl().cabinet.settings.index}
                        className="side-nav-link"
                    >
                        {trans().cabinet.side_navigation.settings}
                    </NavLink>
                </div>
                <div>
                    <Link
                        to={appUrl().logout}
                        className="side-nav-link"
                        onClick={e => {
                            e.preventDefault()
                            logout()
                        }}
                    >
                        {trans().cabinet.side_navigation.logout}
                    </Link>
                </div>
            </CardFooter>
        </>
    )

    return (
        <Card id="cabinet-profile-info">
            <ResponsiveLayout
                desktop={
                    <>
                        <CardBody>
                            <div className="flex-container-row align-items-center">
                                <div className="profile-avatar">
                                    {user.fullName.substring(0, 1)}
                                </div>
                                <div className="ms-3 fw-500">
                                    {user.fullName}
                                </div>
                            </div>
                            <div className="mt-3 flex-row-between flex-wrap">
                                <div className="flex-container-row">
                                    <div className="fw-500">
                                        {user.rating}
                                    </div>
                                    <RatingStars
                                        rating={user.rating}
                                        starSize={20}
                                        className="ms-2 me-2"
                                    />
                                </div>
                                <CabinetUserReviewsButton />
                            </div>
                        </CardBody>
                        {menuContent}
                    </>
                }
                tablet={
                    <>
                        <CardBody className="pe-2 py-2 py-sm-3 flex-row-center-vertical justify-content-between">
                            <div className="flex-container-row align-items-center">
                                <div className="profile-avatar">
                                    {user.fullName.substring(0, 1)}
                                </div>
                                <div className="ms-3 fw-500">
                                    <div>{user.fullName}</div>
                                    <div className="flex-sm-row-between flex-wrap">
                                        <div className="flex-container-row">
                                            <div className="fw-500">
                                                {user.rating}
                                            </div>
                                            <RatingStars
                                                rating={user.rating}
                                                starSize={20}
                                                className="ms-2 me-2"
                                            />
                                        </div>
                                        <CabinetUserReviewsButton
                                            className="ms-sm-2"
                                        />
                                    </div>
                                </div>
                            </div>
                            {!isDesktop && (
                                <IconButton
                                    path={mdiChevronDown}
                                    rotate={isFooterVisible ? 180 : 0}
                                    className="px-2 py-3"
                                    onClick={() => setIsFooterVisible(!isFooterVisible)}
                                />
                            )}
                        </CardBody>
                        <Collapse
                            show={isFooterVisible}
                            showImmediately
                        >
                            {menuContent}
                        </Collapse>
                    </>
                }
            />
        </Card>
    )
}
